import './Header.scss';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CVRequestModal from './CVRequestModal';
import { useTheme } from './ThemeContext';
import * as React from 'react';
import { FaGithub, FaLinkedin, FaEnvelope, FaYoutube, FaTwitter, FaShareAlt, FaMoon, FaSun } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface MenuItem {
  id: string;
  label: string;
  external?: boolean;
  url?: string;
  isChat?: boolean;
}

const Header = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const prevScrollY = useRef(false);

  // Define menu items
  const menuItems: MenuItem[] = useMemo(() => [
    { id: 'profile', label: t('menu.profile') },
    { id: 'projects', label: t('menu.projects') },
    { id: 'showcase', label: t('menu.showcase') },
    { id: 'education', label: t('menu.education') },
    { id: 'writing', label: t('menu.writing') },
    { id: 'badges', label: t('menu.badges') },
    { id: 'contact-me', label: t('menu.contact') },
    { id: 'services', label: t('menu.services'), external: true, url: 'https://devray.qzz.io/' },
    { id: 'ai-chat', label: 'AI Chat', isChat: true }
  ], [t]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 10;
      if (scrolled !== prevScrollY.current) {
        setIsScrolled(scrolled);
        prevScrollY.current = scrolled;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMenuOpen && !target.closest('.menu-container') && !target.closest('.hamburger')) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);
  
  // Handle scroll to detect active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for better detection
      const headerHeight = document.querySelector('.header')?.clientHeight || 0;
      
      // If we're still in the header area, don't highlight any navigation item
      if (scrollPosition < headerHeight - 100) {
        setActiveSection('');
        return;
      }
      
      // Check each section to see if it's in view
      let foundActive = false;
      menuItems.forEach((item) => {
        const section = document.getElementById(item.id);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          const sectionBottom = sectionTop + sectionHeight;
          
          // If scroll position is within this section
          if (scrollPosition >= sectionTop - 100 && scrollPosition < sectionBottom - 100) {
            setActiveSection(item.id);
            foundActive = true;
            return; // Exit once we find the active section
          }
        }
      });
      
      // If no section is active, clear the active section
      if (!foundActive) {
        setActiveSection('');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuItems]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Ray Klanderman - Portfolio',
          text: 'Check out my portfolio!',
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share. Please try again.');
    }
  };

  const handleScrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  // Close menu when a menu item is clicked
  const handleMenuItemClick = (sectionId: string) => {
    handleScrollTo(sectionId);
    setIsMenuOpen(false);
  };

  interface SocialLink {
    icon: React.ReactElement;
    url: string;
    label: string;
    color: string;
  }

  const socialLinks: SocialLink[] = [
    {
      icon: <FaGithub />,
      url: 'https://github.com/rayklanderman',
      label: 'GitHub',
      color: '#58a6ff' // GitHub's light-gray mark, legible on Forest-Ink
    },
    {
      icon: <FaLinkedin />,
      url: 'https://www.linkedin.com/in/rayklanderman/',
      label: 'LinkedIn',
      color: '#0077B5'
    },
    {
      icon: <FaYoutube />,
      url: 'https://www.youtube.com/@RealDevRay',
      label: 'YouTube',
      color: '#FF0000'
    },
    {
      icon: <FaTwitter />,
      url: 'https://x.com/rayklanderman',
      label: 'X (Twitter)',
      color: '#e7e9ea' // X's dark-theme glyph gray, legible on Forest-Ink
    },
    {
      icon: <FaEnvelope />,
      url: 'mailto:rayklanderman@gmail.com',
      label: 'Email',
      color: '#EA4335'
    }
  ];

  return (
    <>
      <header className="header">
        <div className="header-content">
        <div className="profile-image-container">
          <img 
            src="/images/profile1.png" 
            alt="Raymond Klanderman" 
            className="profile-image"
          />
        </div>
        <div className="text-content">
          <h1><span className="highlight">Raymond</span> Klanderman</h1>
          <div className="job-titles">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="job-title"
            >
              {t('header.jobTitles.softwareEngineer', 'Software Engineer')}
            </motion.span>
            <motion.span 
              className="divider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.3 }}
            >
              &nbsp;•&nbsp;
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="job-title"
            >
              {t('header.jobTitles.dataAnalyst', 'Data Analyst')}
            </motion.span>
            <motion.span 
              className="divider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.5 }}
            >
              &nbsp;•&nbsp;
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="job-title"
            >
              {t('header.jobTitles.promptEngineer', 'AI Prompt Engineer')}
            </motion.span>
            <motion.span 
              className="divider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.7 }}
            >
              &nbsp;•&nbsp;
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="job-title"
            >
              {t('header.jobTitles.creative', 'Creative')}
            </motion.span>
          </div>
          <div className="social-links">
            {socialLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                className="social-link"
                style={{ '--brand-color': link.color } as React.CSSProperties}
              >
                {React.cloneElement(link.icon, {
                  className: 'social-icon',
                  size: 24
                })}
              </a>
            ))}
          </div>
          <div className={`menu-container ${isScrolled ? 'scrolled' : ''}`}>
            <div className="menu-content">
              {/* Hamburger button for mobile */}
              <button 
                className={`hamburger ${isMenuOpen ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </button>
              
              {/* Navigation menu */}
              <nav className={`menu-buttons ${isMenuOpen ? 'open' : ''}`}>
                {menuItems.map((item) => (
                  <motion.button
                    key={item.id}
                    className={`menu-button ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => item.external ? window.open(item.url, '_blank') : item.isChat ? navigate('/chat') : handleMenuItemClick(item.id)}
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.label}
                  </motion.button>
                ))}
              </nav>
            </div>
          </div>
          <div className="cta-buttons">
            <button 
              className="theme-toggle" 
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? <FaMoon /> : <FaSun />}
            </button>
            <button className="download-cv" onClick={handleOpenModal}>
              {t('header.downloadCV')}
            </button>
            <button className="share-button" onClick={handleShare}>
              <FaShareAlt /> {t('header.share')}
            </button>
          </div>
          {isModalOpen && <CVRequestModal onClose={handleCloseModal} />}
        </div>
        </div>
      </header>
    </>
  );
};

export default Header;
